import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { TaskType } from "@ptx-autonomous/libs/flightpath-service-api-models";
import type {
	PathPlanningRequest,
	SmootherParams,
} from "@ptx-autonomous/libs/opengl-api-models";
import { Speed } from "@ptx-autonomous/libs/units";
import { useEventListener } from "@vueuse/core";
import { fetchAuthSession } from "aws-amplify/auth";
import {
	computed,
	hasInjectionContext,
	inject,
	isRef,
	ref,
	toValue,
	watch,
	type App,
	type InjectionKey,
	type MaybeRef,
	type MaybeRefOrGetter,
	type Ref,
} from "vue";
import {
	defaultApiVersion,
	defaultEnvironment,
	type Environment,
} from "../configuration/aws-configuration";
import {
	DubinsSteeringStateSpace,
	type SteeringStateSpace,
} from "../types/path-planning";

const usePreferences = Capacitor.isNativePlatform();

// Storage
export async function getValue(key: string) {
	if (usePreferences) return (await Preferences.get({ key })).value;
	else return localStorage.getItem(key);
}

export async function setValue(key: string, value: string) {
	if (usePreferences) await Preferences.set({ key, value });
	else localStorage.setItem(key, value);
}

export async function removeValue(key: string) {
	if (usePreferences) await Preferences.remove({ key });
	else localStorage.removeItem(key);
}

const SETTING_CHANGED_EVENT = "useSetting:changed";

type SettingChangedDetail = { key: string; raw: string | undefined };

// Latest known serialized value per key. `undefined` means either "not yet
// loaded" (key absent from the map) or "loaded, nothing stored" (key present
// with an `undefined` value) - distinguished via `rawCache.has(key)`.
const settingsCache = new Map<string, string | undefined>();

// Creates a standalone ref for a key: seeded from the cache (or storage on
// first use), persisted to storage on change, and kept in sync with every
// other ref for the same key via a `window` event instead of shared state.
function createSetting<T>(
	key: string,
	defaultValue: T | undefined,
): { state: Ref<T | undefined>; ready: Promise<void> } {
	const state = ref(defaultValue) as Ref<T | undefined>;
	let currentRawValue =
		defaultValue === undefined ? undefined : JSON.stringify(defaultValue);

	function setState(raw: string | undefined) {
		try {
			currentRawValue = raw;
			state.value =
				raw === undefined ? defaultValue : (JSON.parse(raw) as T);
		} catch (e) {
			console.error(e);
		}
	}

	// Seed synchronously if another ref already loaded this key.
	if (settingsCache.has(key)) setState(settingsCache.get(key));

	const ready = settingsCache.has(key)
		? Promise.resolve()
		: getValue(key).then(raw => {
				const value = raw ?? undefined;
				settingsCache.set(key, value);
				if (value !== undefined) setState(value);
			});

	useEventListener(window, SETTING_CHANGED_EVENT, event => {
		const detail = (event as CustomEvent<SettingChangedDetail>).detail;
		if (detail.key !== key || detail.raw === currentRawValue) return;
		settingsCache.set(key, detail.raw);
		setState(detail.raw);
	});

	watch(
		state,
		value => {
			const serialized =
				value === undefined ? undefined : JSON.stringify(value);
			if (serialized === currentRawValue) return;
			currentRawValue = serialized;
			settingsCache.set(key, serialized);
			if (serialized === undefined) removeValue(key);
			else setValue(key, serialized);
			window.dispatchEvent(
				new CustomEvent<SettingChangedDetail>(SETTING_CHANGED_EVENT, {
					detail: { key, raw: serialized },
				}),
			);
		},
		{ deep: true, flush: "sync" },
	);

	return { state, ready };
}

function createReactiveKeySetting<T>(
	key: Ref<string | undefined>,
	defaultValue: T | undefined,
): { state: Ref<T | undefined>; ready: () => Promise<void> } {
	const state = ref(defaultValue) as Ref<T | undefined>;
	let currentKey: string | undefined;
	let currentRawValue: string | undefined;
	let ready: Promise<void> = Promise.resolve();

	function setState(raw: string | undefined) {
		try {
			currentRawValue = raw;
			state.value =
				raw === undefined ? defaultValue : (JSON.parse(raw) as T);
		} catch (e) {
			console.error(e);
		}
	}

	function load(newKey: string | undefined) {
		currentKey = newKey;
		currentRawValue =
			defaultValue === undefined
				? undefined
				: JSON.stringify(defaultValue);
		state.value = defaultValue;
		if (newKey === undefined) {
			ready = Promise.resolve();
			return;
		}
		if (settingsCache.has(newKey)) {
			setState(settingsCache.get(newKey));
			ready = Promise.resolve();
			return;
		}
		ready = getValue(newKey).then(raw => {
			const value = raw ?? undefined;
			settingsCache.set(newKey, value);
			// Ignore a late load for a key we've since moved away from.
			if (newKey === currentKey && value !== undefined) setState(value);
		});
	}

	useEventListener(window, SETTING_CHANGED_EVENT, (event: Event) => {
		const detail = (event as CustomEvent<SettingChangedDetail>).detail;
		if (detail.key !== currentKey || detail.raw === currentRawValue) return;
		settingsCache.set(detail.key, detail.raw);
		setState(detail.raw);
	});

	watch(() => toValue(key), load, { immediate: true });

	watch(
		state,
		value => {
			if (currentKey === undefined) return;
			const serialized =
				value === undefined ? undefined : JSON.stringify(value);
			if (serialized === currentRawValue) return;
			currentRawValue = serialized;
			settingsCache.set(currentKey, serialized);
			if (serialized === undefined) removeValue(currentKey);
			else setValue(currentKey, serialized);
			window.dispatchEvent(
				new CustomEvent<SettingChangedDetail>(SETTING_CHANGED_EVENT, {
					detail: { key: currentKey, raw: serialized },
				}),
			);
		},
		{ deep: true, flush: "sync" },
	);

	return { state, ready: () => ready };
}

/**
 * Reactive, persisted setting backed by localStorage (web) or the Preferences
 * API (native), serialized as JSON. Every call creates its own ref; writing
 * through any of them persists the value and syncs every other ref for the
 * same key via a `window` event.
 *
 * `key` may be a reactive ref; when it changes the ref reloads the value for
 * the new key, and writes always persist under the current key. When the key
 * is `undefined` the ref holds the default value and nothing is persisted.
 *
 * The stored value is loaded asynchronously, so on native (Preferences API)
 * the ref holds the default/undefined until the load resolves. Use
 * `useSettingAsync` when the initial value must be present before use.
 */
export function useSetting<T>(
	key: MaybeRef<string | undefined>,
	defaultValue: T,
): Ref<T>;
export function useSetting<T>(
	key: MaybeRef<string | undefined>,
): Ref<T | undefined>;
export function useSetting<T>(
	key: MaybeRef<string | undefined>,
	defaultValue?: T,
): Ref<T | undefined> {
	if (isRef(key)) return createReactiveKeySetting(key, defaultValue).state;
	if (key === undefined) return ref(defaultValue) as Ref<T | undefined>;
	return createSetting<T>(key, defaultValue).state;
}

/**
 * Like `useSetting`, but awaits the stored value before returning so the ref is
 * never the default/undefined when a value is already persisted.
 */
export function useSettingAsync<T>(
	key: MaybeRef<string | undefined>,
	defaultValue: T,
): Promise<Ref<T>>;
export function useSettingAsync<T>(
	key: MaybeRef<string | undefined>,
): Promise<Ref<T | undefined>>;
export async function useSettingAsync<T>(
	key: MaybeRef<string | undefined>,
	defaultValue?: T,
): Promise<Ref<T | undefined>> {
	if (isRef(key)) {
		const { state, ready } = createReactiveKeySetting(key, defaultValue);
		if (toValue(key) !== undefined) await ready();
		return state;
	}
	if (key === undefined) return ref(defaultValue) as Ref<T | undefined>;
	const { state, ready } = createSetting<T>(key, defaultValue);
	await ready;
	return state;
}

/**
 * Reads a setting's current value without creating a reactive ref, using the
 * cached value if one has been fetched, otherwise reading from storage.
 */
export function getSettingAsync<T>(key: string, defaultValue: T): Promise<T>;
export function getSettingAsync<T>(key: string): Promise<T | undefined>;
export async function getSettingAsync<T>(
	key: string,
	defaultValue?: T,
): Promise<T | undefined> {
	if (!settingsCache.has(key))
		settingsCache.set(key, (await getValue(key)) ?? undefined);
	const raw = settingsCache.get(key);
	if (raw == null) return defaultValue;
	try {
		return JSON.parse(raw) as T;
	} catch (e) {
		console.log(e);
		return defaultValue;
	}
}

/**
 * Writes a setting's value without creating a reactive ref, updating the
 * cache, storage, and notifying any `useSetting` refs for the same key.
 * Passing `undefined` removes the stored value.
 */
export async function setSettingAsync<T>(key: string, value: T | undefined) {
	const serialized = value === undefined ? undefined : JSON.stringify(value);
	settingsCache.set(key, serialized);
	if (serialized === undefined) await removeValue(key);
	else await setValue(key, serialized);
	window.dispatchEvent(
		new CustomEvent<SettingChangedDetail>(SETTING_CHANGED_EVENT, {
			detail: { key, raw: serialized },
		}),
	);
}

const networkSettingsKey = "settings.network";
const defaultNetworkSettings = {
	machineIP: "192.168.10.1",
	fpPort: "3581",
	mqttPort: "9001",
	updaterPort: "2077",
	windToolsPort: "3578",
	cameraCalibrationPort: "4000",
};
export const useNetworkSettings = () =>
	useSetting(networkSettingsKey, defaultNetworkSettings);
export const useNetworkSettingsAsync = () =>
	useSettingAsync(networkSettingsKey, defaultNetworkSettings);
export const getNetworkSettingsAsync = () =>
	getSettingAsync(networkSettingsKey, defaultNetworkSettings);
export const setNetworkSettingsAsync = (value: typeof defaultNetworkSettings) =>
	setSettingAsync(networkSettingsKey, value);

// Environment
const environmentKey = "settings.environment";
export const useEnvironmentSetting = () =>
	useSetting(environmentKey, defaultEnvironment);
export const useEnvironmentSettingAsync = () =>
	useSettingAsync(environmentKey, defaultEnvironment);
export const getEnvironmentSettingAsync = () =>
	getSettingAsync(environmentKey, defaultEnvironment);
export const setEnvironmentSettingAsync = (value: Environment) =>
	setSettingAsync(environmentKey, value);

// ApiVersion (new-pattern functions)
const apiVersionKey = "settings.apiVersion";
const providedApiVersionKey: InjectionKey<Ref<string>> =
	Symbol("providedApiVersion");
export function provideApiVersionSetting(app: App, apiVersion: Ref<string>) {
	app.provide(providedApiVersionKey, apiVersion);
}
export function useApiVersionSetting() {
	if (hasInjectionContext()) {
		const apiVersion = inject(providedApiVersionKey, undefined);
		if (apiVersion) return apiVersion;
	}

	return useSetting(apiVersionKey, defaultApiVersion as string);
}
export const useApiVersionSettingAsync = () =>
	useSettingAsync(apiVersionKey, defaultApiVersion as string);
export const getApiVersionSettingAsync = () =>
	getSettingAsync(apiVersionKey, defaultApiVersion as string);
export const setApiVersionSettingAsync = (value: string) =>
	setSettingAsync(apiVersionKey, value);

// Organization (new-pattern functions, keyed per user)
async function organizationSettingKeyFromSession() {
	const session = await Promise.race([
		fetchAuthSession(),
		new Promise<never>((_, reject) =>
			setTimeout(
				() => reject(new Error("fetchAuthSession timed out")),
				3000,
			),
		),
	]);
	const email = session.tokens?.idToken?.payload.email;
	if (!email) return undefined;
	return `settings.organizationId.${email}`;
}
export const useOrganizationSettingAsync = async () => {
	const key = await organizationSettingKeyFromSession();
	if (!key) return ref("");
	return useSettingAsync(key, "");
};
export const getOrganizationSettingAsync = async () => {
	const key = await organizationSettingKeyFromSession();
	if (!key) return undefined;
	return getSettingAsync(key, "");
};
export const setOrganizationSettingAsync = async (value: string) => {
	const key = await organizationSettingKeyFromSession();
	if (!key) return;
	await setSettingAsync(key, value);
};

// Machine speeds
type MachineSpeedSettingsInternal = {
	stage: number;
	unload: number;
	sendToTruck: number;
	followBehindHarvester: number;
};

export type MachineSpeedSettings = {
	stage: Speed;
	unload: Speed;
	sendToTruck: Speed;
	followBehindHarvester: Speed;
};

const machineSpeedSettingsKey = (machineId: string) =>
	`settings.machineSpeeds.${machineId}`;
const defaultMachineSpeedSettings: MachineSpeedSettingsInternal = {
	stage: 10,
	unload: 10,
	sendToTruck: 10,
	followBehindHarvester: 10,
};

function machineSpeedSettingsFromInternal(
	internal: MachineSpeedSettingsInternal,
): MachineSpeedSettings {
	return {
		stage: new Speed(Math.round(internal.stage), "mph"),
		unload: new Speed(Math.round(internal.unload), "mph"),
		sendToTruck: new Speed(Math.round(internal.sendToTruck), "mph"),
		followBehindHarvester: new Speed(
			Math.round(internal.followBehindHarvester),
			"mph",
		),
	};
}

function machineSpeedSettingsToInternal(
	settings: MachineSpeedSettings,
): MachineSpeedSettingsInternal {
	return {
		stage: Math.round(settings.stage.mph()),
		unload: Math.round(settings.unload.mph()),
		sendToTruck: Math.round(settings.sendToTruck.mph()),
		followBehindHarvester: Math.round(settings.followBehindHarvester.mph()),
	};
}

export const useMachineSpeedSettings = (machineId: MaybeRef<string>) =>
	useSetting(
		computed(() => machineSpeedSettingsKey(toValue(machineId))),
		defaultMachineSpeedSettings,
	);
export const useMachineSpeedSettingsAsync = (machineId: MaybeRef<string>) =>
	useSettingAsync(
		computed(() => machineSpeedSettingsKey(toValue(machineId))),
		defaultMachineSpeedSettings,
	);

export const getMachineSpeedSettingsAsync = async (
	machineId: string,
): Promise<MachineSpeedSettings> =>
	machineSpeedSettingsFromInternal(
		await getSettingAsync(
			machineSpeedSettingsKey(machineId),
			defaultMachineSpeedSettings,
		),
	);
export const setMachineSpeedSettingsAsync = (
	machineId: string,
	value: MachineSpeedSettings,
) =>
	setSettingAsync(
		machineSpeedSettingsKey(machineId),
		machineSpeedSettingsToInternal(value),
	);
export const getSpeedForTaskTypeAsync = async (
	machineId: string,
	taskType: TaskType,
): Promise<Speed> => {
	const settings = await getMachineSpeedSettingsAsync(machineId);
	switch (taskType) {
		case TaskType.StageCart:
			return settings.stage;
		case TaskType.UnloadToCart:
			return settings.unload;
		case TaskType.SendToTruck:
			return settings.sendToTruck;
		case TaskType.FollowBehindHarvester:
			return settings.followBehindHarvester;
		default:
			throw new Error(`Unsupported task type: ${taskType}`);
	}
};

// 3D view (new-pattern functions)
const threeDViewEnabledKey = "settings.3DViewEnabled";
export const use3DViewEnabledSetting = () =>
	useSetting(threeDViewEnabledKey, false);
export const use3DViewEnabledSettingAsync = () =>
	useSettingAsync(threeDViewEnabledKey, false);
export const get3DViewEnabledSettingAsync = () =>
	getSettingAsync(threeDViewEnabledKey, false);
export const set3DViewEnabledSettingAsync = (value: boolean) =>
	setSettingAsync(threeDViewEnabledKey, value);

// Allow non-RTK recorded boundaries (new-pattern functions)
const allowNonRtkRecordedBoundariesKey =
	"settings.allowNonRtkRecordedBoundaries";
export const useAllowNonRtkRecordedBoundariesSetting = () =>
	useSetting(allowNonRtkRecordedBoundariesKey, false);
export const useAllowNonRtkRecordedBoundariesSettingAsync = () =>
	useSettingAsync(allowNonRtkRecordedBoundariesKey, false);
export const getAllowNonRtkRecordedBoundariesSettingAsync = () =>
	getSettingAsync(allowNonRtkRecordedBoundariesKey, false);
export const setAllowNonRtkRecordedBoundariesSettingAsync = (value: boolean) =>
	setSettingAsync(allowNonRtkRecordedBoundariesKey, value);

// Send cloud gamepad inputs
const sendCloudGamepadInputsKey = "settings.sendCloudGamepadInputs";
export const useSendCloudGamepadInputsSetting = () =>
	useSetting(sendCloudGamepadInputsKey, false);
export const useSendCloudGamepadInputsSettingAsync = () =>
	useSettingAsync(sendCloudGamepadInputsKey, false);
export const getSendCloudGamepadInputsSettingAsync = () =>
	getSettingAsync(sendCloudGamepadInputsKey, false);
export const setSendCloudGamepadInputsSettingAsync = (value: boolean) =>
	setSettingAsync(sendCloudGamepadInputsKey, value);

// Virtual gamepad (new-pattern functions)
const virtualGamepadEnabledKey = "settings.virtualGamepad";
export const useVirtualGamepadEnabledSetting = () =>
	useSetting(virtualGamepadEnabledKey, false);
export const useVirtualGamepadEnabledSettingAsync = () =>
	useSettingAsync(virtualGamepadEnabledKey, false);
export const getVirtualGamepadEnabledSettingAsync = () =>
	getSettingAsync(virtualGamepadEnabledKey, false);
export const setVirtualGamepadEnabledSettingAsync = (value: boolean) =>
	setSettingAsync(virtualGamepadEnabledKey, value);

// App notifications
const appNotificationsEnabledKey = "settings.appNotificationsEnabled";
const appNotificationMissionIdsKey = (organizationId: string) =>
	`settings.appNotificationMissionIds.${organizationId}`;
const appNotificationMachineIdsKey = (organizationId: string) =>
	`settings.appNotificationMachineIds.${organizationId}`;
export function useAppNotificationSettings(
	organizationId: MaybeRef<string | undefined>,
) {
	const enabled = useSetting(appNotificationsEnabledKey, false);
	const missionIds = useSetting<string[]>(
		computed(() => {
			const currentOrganizationId = toValue(organizationId);
			return currentOrganizationId
				? appNotificationMissionIdsKey(currentOrganizationId)
				: undefined;
		}),
	);
	const machineIds = useSetting<string[]>(
		computed(() => {
			const currentOrganizationId = toValue(organizationId);
			return currentOrganizationId
				? appNotificationMachineIdsKey(currentOrganizationId)
				: undefined;
		}),
	);

	return { enabled, missionIds, machineIds };
}

// Nudging cart (new-pattern functions)
const nudgingCartSettingKey = "settings.nudgingCart";
export const useNudgingCartSetting = () =>
	useSetting(nudgingCartSettingKey, false);
export const useNudgingCartSettingAsync = () =>
	useSettingAsync(nudgingCartSettingKey, false);
export const getNudgingCartSettingAsync = () =>
	getSettingAsync(nudgingCartSettingKey, false);
export const setNudgingCartSettingAsync = (value: boolean) =>
	setSettingAsync(nudgingCartSettingKey, value);

// Task control (new-pattern functions, keyed per mission)
function taskControlEnabledKey(missionId: string): string;
function taskControlEnabledKey(
	missionId: string | undefined,
): string | undefined;
function taskControlEnabledKey(missionId?: string): string | undefined {
	return missionId ? `settings.taskControlEnabled.${missionId}` : undefined;
}
// Undefined means the user has not manually overridden the deployer-based default.
export const useTaskControlEnabledSetting = (
	missionId: Ref<string | undefined>,
) => {
	const key = computed(() => taskControlEnabledKey(missionId.value));
	const value = useSetting<boolean>(key);
	const isLoading = ref(false);
	const error = ref<unknown>();
	let loadId = 0;

	watch(
		key,
		async newKey => {
			const currentLoadId = ++loadId;
			error.value = undefined;
			if (!newKey) {
				isLoading.value = false;
				return;
			}

			isLoading.value = true;
			try {
				await getSettingAsync<boolean>(newKey);
			} catch (caughtError) {
				if (currentLoadId !== loadId) return;
				console.error(
					"Failed to load task control setting",
					caughtError,
				);
				error.value = caughtError;
			} finally {
				if (currentLoadId === loadId) isLoading.value = false;
			}
		},
		{ immediate: true },
	);

	return { value, isLoading, error };
};
export const useTaskControlEnabledSettingAsync = (missionId: Ref<string>) =>
	useSettingAsync<boolean>(
		computed(() => taskControlEnabledKey(missionId.value)),
	);
export const getTaskControlEnabledSettingAsync = (missionId: string) =>
	getSettingAsync<boolean>(taskControlEnabledKey(missionId));
export const setTaskControlEnabledSettingAsync = (
	missionId: string,
	value: boolean,
) => setSettingAsync(taskControlEnabledKey(missionId), value);

// Stage crop offset / buffer width in feet (keyed per mission)
function stageBufferWidthFtKey(missionId: string | undefined) {
	return missionId ? `settings.stageBufferWidthFt.${missionId}` : undefined;
}
export const useStageBufferWidthFtSetting = (
	missionId: Ref<string | undefined>,
) => useSetting<number>(computed(() => stageBufferWidthFtKey(missionId.value)));

export const useObstacleDetectionOverrideSetting = () =>
	useSetting("settings.obstacleDetectionOverride", false);
export const useObstacleDetectionOverrideSettingAsync = () =>
	useSettingAsync("settings.obstacleDetectionOverride", false);
export const getObstacleDetectionOverrideSettingAsync = () =>
	getSettingAsync("settings.obstacleDetectionOverride", false);
export const setObstacleDetectionOverrideSettingAsync = (value: boolean) =>
	setSettingAsync("settings.obstacleDetectionOverride", value);

export const useSteeringStateSpaceSetting = () =>
	useSetting<SteeringStateSpace>(
		"settings.steeringStateSpace",
		DubinsSteeringStateSpace,
	);
export const getSteeringStateSpaceSettingAsync = () =>
	getSettingAsync<SteeringStateSpace>(
		"settings.steeringStateSpace",
		DubinsSteeringStateSpace,
	);

export const useAllowManualObstacleCompletionSetting = () =>
	useSetting("settings.allowManualObstacleCompletion", false);
export const useAllowManualObstacleCompletionSettingAsync = () =>
	useSettingAsync("settings.allowManualObstacleCompletion", false);
export const getAllowManualObstacleCompletionSettingAsync = () =>
	getSettingAsync("settings.allowManualObstacleCompletion", false);
export const setAllowManualObstacleCompletionSettingAsync = (value: boolean) =>
	setSettingAsync("settings.allowManualObstacleCompletion", value);

const enableTillageImplementTurnsKey = "settings.enableTillageImplementTurns";
export const useEnableTillageImplementTurnsSetting = () =>
	useSetting(enableTillageImplementTurnsKey, false);
export const getEnableTillageImplementTurnsSettingAsync = () =>
	getSettingAsync(enableTillageImplementTurnsKey, false);

export const defaultPathPlanningSettings: Pick<
	PathPlanningRequest,
	"TurningRadius" | "CurvatureRate"
> &
	SmootherParams = {
	TurningRadius: 10,
	CurvatureRate: 0.1,
	NeighborRadius: 40,
	MinNeighborDistance: 15,
	IntermediateSampleSpacing: 5,
	CrossTrackSamples: 7,
	LengthWeight: 1,
	CurvatureRateWeight: 30,
	CrossTrackWeight: 1.5,
	CurvatureWeight: 0.75,
};
export const usePathPlanningSettings = () =>
	useSetting("settings.pathPlanning", defaultPathPlanningSettings);
export const usePathPlanningSettingsAsync = () =>
	useSettingAsync("settings.pathPlanning", defaultPathPlanningSettings);
export const getPathPlanningSettingsAsync = () =>
	getSettingAsync("settings.pathPlanning", defaultPathPlanningSettings);
export const setPathPlanningSettingsAsync = (
	value: Pick<PathPlanningRequest, "TurningRadius" | "CurvatureRate"> &
		SmootherParams,
) => setSettingAsync("settings.pathPlanning", value);

const defaultMachineColor = "var(--color-white)";
export const useMachineColorSetting = (
	machineId: MaybeRefOrGetter<string | undefined>,
) =>
	useSetting(
		computed(() => {
			const machineIdValue = toValue(machineId);
			return machineIdValue
				? `settings.machineColor.${machineIdValue}`
				: undefined;
		}),
		defaultMachineColor,
	);

const defaultMachineBackgroundColor = "var(--color-green-600)";
export const useMachineBackgroundColorSetting = (
	machineId: MaybeRefOrGetter<string | undefined>,
) =>
	useSetting(
		computed(() => {
			const machineIdValue = toValue(machineId);
			return machineIdValue
				? `settings.machineColorBackground.${machineIdValue}`
				: undefined;
		}),
		defaultMachineBackgroundColor,
	);

const forceDirectUdpKey = "settings.forceDirectUdp";
export const useForceDirectUdpSetting = () =>
	useSetting(forceDirectUdpKey, false);
export const getForceDirectUdpSettingAsync = () =>
	getSettingAsync(forceDirectUdpKey, false);

export const useAutoModeNotificationSetting = () =>
	useSetting("settings.autoModeNotification", true);
export const useAutoModeNotificationSettingAsync = () =>
	useSettingAsync("settings.autoModeNotification", true);
export const getAutoModeNotificationSettingAsync = () =>
	getSettingAsync("settings.autoModeNotification", true);
export const setAutoModeNotificationSettingAsync = (value: boolean) =>
	setSettingAsync("settings.autoModeNotification", value);

export const useCartPathPlanningSetting = () =>
	useSetting("settings.cartPathPlanning", true);
export const useCartPathPlanningSettingAsync = () =>
	useSettingAsync("settings.cartPathPlanning", true);
export const getCartPathPlanningSettingAsync = () =>
	getSettingAsync("settings.cartPathPlanning", true);
export const setCartPathPlanningSettingAsync = (value: boolean) =>
	setSettingAsync("settings.cartPathPlanning", value);

export const usePathPlanningPreferHeadlandsSetting = () =>
	useSetting("settings.pathPlanningPreferHeadlands", true);
export const usePathPlanningPreferHeadlandsSettingAsync = () =>
	useSettingAsync("settings.pathPlanningPreferHeadlands", true);
export const getPathPlanningPreferHeadlandsSettingAsync = () =>
	getSettingAsync("settings.pathPlanningPreferHeadlands", true);
export const setPathPlanningPreferHeadlandsSettingAsync = (value: boolean) =>
	setSettingAsync("settings.pathPlanningPreferHeadlands", value);

export type MapViewMode = "simplified" | "2D" | "3D";
export const useMapViewModeSetting = () =>
	useSetting("settings.mapViewMode", "simplified" as MapViewMode);
export const useMapViewModeSettingAsync = () =>
	useSettingAsync("settings.mapViewMode", "simplified" as MapViewMode);
export const getMapViewModeSettingAsync = () =>
	getSettingAsync("settings.mapViewMode", "simplified" as MapViewMode);
export const setMapViewModeSettingAsync = (value: MapViewMode) =>
	setSettingAsync("settings.mapViewMode", value);

export type MachineTabSetting =
	| "alignment"
	| "machine"
	| "ready-checks"
	| "speeds"
	| "wheel-slip"
	| "mission"
	| "cameras";
export const useSelectedTabsSetting = () =>
	useSetting("settings.selectedTabs", [] as MachineTabSetting[]);
export const useSelectedTabsSettingAsync = () =>
	useSettingAsync("settings.selectedTabs", [] as MachineTabSetting[]);
export const getSelectedTabsSettingAsync = () =>
	getSettingAsync("settings.selectedTabs", [] as MachineTabSetting[]);
export const setSelectedTabsSettingAsync = (value: MachineTabSetting[]) =>
	setSettingAsync("settings.selectedTabs", value);
export const resetSelectedTabsSettingAsync = () =>
	setSettingAsync("settings.selectedTabs", [] as MachineTabSetting[]);
